import React, { useState, useMemo, useEffect, useDeferredValue } from 'react';
import { Modal, Form, Row, Col, Table, InputGroup, Spinner, Pagination } from 'react-bootstrap';

const ClientSearchModal = ({ show, onHide, onSelectClient, clients = [], sellers = [], isLoading = false, user }) => {
    const loggedSeller = useMemo(() => {
        if (!user?.c_vende || !sellers || sellers.length === 0) return null;
        return sellers.find(s => String(s.c_vende).trim() === String(user.c_vende).trim());
    }, [sellers, user]);

    const isVendorOnly = useMemo(() => {
        if (!user?.c_vende || user?.l_admin) return false;
        if (loggedSeller) {
            return Boolean(loggedSeller.l_filven);
        }
        return false;
    }, [user, loggedSeller]);

    const [clientSearch, setClientSearch] = useState('');
    const deferredClientSearch = useDeferredValue(clientSearch);
    const [clientDocTypeFilter, setClientDocTypeFilter] = useState('');
    const [clientVendeFilter, setClientVendeFilter] = useState(isVendorOnly ? String(user.c_vende) : '');
    const [page, setPage] = useState(1);
    const itemsPerPage = 15;

    // Sincronizar clientVendeFilter al abrir el modal
    useEffect(() => {
        if (show) {
            setClientSearch('');
            setClientDocTypeFilter('');
            setClientVendeFilter(isVendorOnly ? String(user.c_vende) : '');
            setPage(1);
        }
    }, [show, isVendorOnly, user]);

    useEffect(() => {
        setPage(1);
    }, [deferredClientSearch, clientDocTypeFilter, clientVendeFilter]);

    // Extraemos tipos de documentos únicos para el filtro
    const availableDocTypes = useMemo(() => {
        const types = new Set(clients.map(c => String(c.c_docum || '').trim()).filter(Boolean));
        return Array.from(types).sort();
    }, [clients]);

    // Extraemos códigos de vendedores únicos para el filtro, excluyendo vendedores anulados
    const availableSellers = useMemo(() => {
        const uniqueCodes = new Set(clients.map(c => c.c_vende !== undefined && c.c_vende !== null ? String(c.c_vende).trim() : '').filter(Boolean));
        return Array.from(uniqueCodes)
            .filter(code => {
                const found = sellers.find(s => String(s.c_vende).trim() === code);
                return !found || parseInt(found.c_situa, 10) !== 9;
            })
            .sort((a, b) => parseFloat(a) - parseFloat(b));
    }, [clients, sellers]);

    // Opcional: Diccionario para mapear los códigos si sabés cuáles son
    const docTypeMap = {
        '80': 'CUIT (80)',
        '96': 'DNI (96)',
        '99': 'Sin Identificar (99)',
        '86': 'CUIL (86)'
    };
    const getDocTypeName = (code) => docTypeMap[code] || `Doc. Código ${code}`;

    const getSellerName = (code) => {
        const found = sellers.find(s => String(s.c_vende).trim() === String(code).trim());
        return found ? `${found.d_apell || ''}, ${found.d_nombr || ''} (${code})`.trim() : `Vendedor ${code}`;
    };

    const filteredClients = useMemo(() => {
        let filtered = [...clients]; // Evitar mutar el prop original

        // Excluir clientes anulados (c_situa === 9)
        filtered = filtered.filter(c => parseInt(c.c_situa, 10) !== 9);

        if (clientDocTypeFilter) {
            filtered = filtered.filter(c => String(c.c_docum || '').trim() === clientDocTypeFilter);
        }

        if (clientVendeFilter) {
            filtered = filtered.filter(c => c.c_vende !== undefined && c.c_vende !== null && String(c.c_vende).trim() === clientVendeFilter);
        }

        if (deferredClientSearch) {
            const lowercasedSearch = deferredClientSearch.toLowerCase().trim();
            // Filtrar clientes
            filtered = filtered.filter(c =>
                String(c.c_clien).toLowerCase().includes(lowercasedSearch) ||
                (c.d_razon && c.d_razon.toLowerCase().includes(lowercasedSearch)) ||
                (c.d_razfan && c.d_razfan.toLowerCase().includes(lowercasedSearch)) ||
                (c.d_domic && c.d_domic.toLowerCase().includes(lowercasedSearch)) ||
                (c.n_telef && c.n_telef.toLowerCase().includes(lowercasedSearch)) ||
                (c.n_celul && c.n_celul.toLowerCase().includes(lowercasedSearch)) ||
                (c.d_whatsapp && c.d_whatsapp.toLowerCase().includes(lowercasedSearch)) ||
                String(c.n_docum || '').toLowerCase().includes(lowercasedSearch)
            );

            // Función para puntuar la relevancia de cada coincidencia
            const getSearchScore = (client, search) => {
                const searchStr = String(search).toLowerCase();
                const idStr = String(client.c_clien).toLowerCase();
                const razonStr = String(client.d_razon || '').toLowerCase();
                const razfanStr = String(client.d_razfan || '').toLowerCase();
                
                // 1. Coincidencia exacta de ID
                if (idStr === searchStr) return 100;
                // 2. ID contiene el dígito de búsqueda
                if (idStr.includes(searchStr)) return 80;
                // 3. Razón social / Fantasía empieza con la búsqueda
                if (razonStr.startsWith(searchStr) || razfanStr.startsWith(searchStr)) return 70;
                // 4. Razón social / Fantasía contiene la búsqueda
                if (razonStr.includes(searchStr) || razfanStr.includes(searchStr)) return 60;
                // 5. Coincidencias secundarias (otros campos)
                return 10;
            };

            // Ordenar por relevancia y luego por ID numérico ascendente
            filtered.sort((a, b) => {
                const scoreA = getSearchScore(a, deferredClientSearch);
                const scoreB = getSearchScore(b, deferredClientSearch);
                
                if (scoreA !== scoreB) {
                    return scoreB - scoreA;
                }
                return parseInt(a.c_clien, 10) - parseInt(b.c_clien, 10);
            });
        } else {
            // Si no hay filtro de búsqueda, ordenar simplemente por ID de forma ascendente
            filtered.sort((a, b) => parseInt(a.c_clien, 10) - parseInt(b.c_clien, 10));
        }

        return filtered;
    }, [clients, deferredClientSearch, clientDocTypeFilter, clientVendeFilter]);

    const paginatedClients = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        return filteredClients.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredClients, page, itemsPerPage]);

    const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

    const handleSelect = (client) => {
        onSelectClient(client);
        handleClose();
    };
    
    const handleClose = () => {
        setClientSearch('');
        setClientDocTypeFilter('');
        setClientVendeFilter(isVendorOnly ? String(user.c_vende) : '');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title><i className="bi bi-people me-2"></i>Buscar Cliente</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-3 g-2">
                    <Col md={6}>
                        <InputGroup>
                            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                            <Form.Control
                                placeholder="Buscar por N°, Razón Social, Fantasía, Dirección, Documento..."
                                value={clientSearch}
                                onChange={(e) => setClientSearch(e.target.value)}
                                autoFocus
                            />
                        </InputGroup>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={clientDocTypeFilter}
                            onChange={(e) => setClientDocTypeFilter(e.target.value)}
                        >
                            <option value="">Tipo Documento</option>
                            {availableDocTypes.map(type => (
                                <option key={type} value={type}>{getDocTypeName(type)}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={clientVendeFilter}
                            onChange={(e) => setClientVendeFilter(e.target.value)}
                            disabled={isVendorOnly}
                        >
                            <option value="">Todos los Vendedores</option>
                            {availableSellers.map(v => (
                                <option key={v} value={v}>{getSellerName(v)}</option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
                
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <span className="ms-3 text-muted">Cargando clientes, por favor espere...</span>
                    </div>
                ) : (
                    <>
                        <div style={{ maxHeight: '420px', overflow: 'auto' }}>
                            <Table striped bordered hover style={{ minWidth: '900px', marginBottom: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                    <tr>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>N°</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Razón Social</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Fantasía</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Dirección</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Teléfonos / Cel.</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>WhatsApp</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Doc.</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>N° Doc.</th>
                                        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>IVA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedClients.map((c) => (
                                        <tr key={c.c_clien} onClick={() => handleSelect(c)} style={{ cursor: 'pointer' }}>
                                            <td className="fw-bold">{c.c_clien}</td>
                                            <td>{c.d_razon}</td>
                                            <td>{c.d_razfan}</td>
                                            <td>{c.d_domic}</td>
                                            <td>{[c.n_telef, c.n_celul].filter(Boolean).join(' / ')}</td>
                                            <td>{c.d_whatsapp}</td>
                                            <td>{c.c_docum}</td>
                                            <td>{c.n_docum}</td>
                                            <td>{c.c_iva}</td>
                                        </tr>
                                    ))}
                                    {filteredClients.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="text-center text-muted py-4">
                                                No se encontraron clientes que coincidan con la búsqueda.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                    Mostrando {(page - 1) * itemsPerPage + 1} - {Math.min(page * itemsPerPage, filteredClients.length)} de {filteredClients.length} clientes
                                </small>
                                <Pagination className="mb-0">
                                    <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (totalPages > 5 && page > 3) {
                                            pageNum = Math.min(page - 2 + i, totalPages - 4 + i);
                                        }
                                        return (
                                            <Pagination.Item key={pageNum} active={pageNum === page} onClick={() => setPage(pageNum)}>
                                                {pageNum}
                                            </Pagination.Item>
                                        );
                                    })}
                                    <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ClientSearchModal;
